/**
 * Mutaciones local-first de TUTORÍAS (M3).
 *
 * Escriben en PowerSync → write-path (`db/sync.rs::submission_version`), que
 * aplica la authz REAL:
 *   • nueva versión: alumno matriculado ACTIVO sobre su propia entrega (respeta
 *     `allow_resubmission`/`max_versions`; la anterior debe estar DEVUELTA);
 *   • revisión (feedback+outcome): revisor de la cohorte (tutor/lead/assistant/
 *     admin); `feedback_by` lo SELLA el servidor.
 *
 * La config de SLA/reentrega se ESCRIBE por REST (no aquí): ver el cliente REST
 * `features/academy/tutor/api.ts`.
 */

import { and, desc, eq, type PowerSyncDrizzleDB, submission_version } from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateUUID } from "../../utils/uuid";

/** Máxima versión existente de una entrega (0 si aún no hay ninguna). */
async function currentMaxVersion(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  cohortId: string,
  userId: string,
): Promise<{ version: number; outcome: string | null } | null> {
  const rows = await db
    .select({ version: submission_version.version, outcome: submission_version.outcome })
    .from(submission_version)
    .where(
      and(
        eq(submission_version.assignment_id, assignmentId),
        eq(submission_version.cohort_id, cohortId),
        eq(submission_version.user_id, userId),
      ),
    )
    .orderBy(desc(submission_version.version))
    .limit(1);
  const r = rows[0];
  return r ? { version: r.version ?? 0, outcome: r.outcome ?? null } : null;
}

/**
 * El ALUMNO crea una NUEVA versión de su entrega (reentrega). El servidor
 * recalcula el nº de versión autoritativo y sella `late`; aquí sólo optimista.
 * Devuelve el id de la versión creada.
 */
export async function submitNewVersionMutation(
  db: PowerSyncDrizzleDB,
  params: {
    assignmentId: string;
    cohortId: string;
    organizationId: string;
    userId: string;
    body?: string | null;
    files?: string[];
    groupId?: string | null;
  },
): Promise<string> {
  const now = nowISO();
  const latest = await currentMaxVersion(db, params.assignmentId, params.cohortId, params.userId);
  const version = (latest?.version ?? 0) + 1;
  const id = generateUUID();
  await db.insert(submission_version).values({
    id,
    assignment_id: params.assignmentId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    user_id: params.userId,
    group_id: params.groupId ?? null,
    version,
    body: params.body ?? null,
    files: JSON.stringify(params.files ?? []),
    submitted_at: now,
    outcome: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Edita el BORRADOR pendiente (misma versión) antes de que el tutor la revise.
 * El servidor lo permite sólo si sigue pendiente (outcome IS NULL) y es del actor.
 */
export async function updatePendingVersionMutation(
  db: PowerSyncDrizzleDB,
  versionId: string,
  updates: { body?: string | null; files?: string[] },
): Promise<void> {
  const patch: Record<string, unknown> = { submitted_at: nowISO(), updated_at: nowISO() };
  if (updates.body !== undefined) {
    patch.body = updates.body;
  }
  if (updates.files !== undefined) {
    patch.files = JSON.stringify(updates.files);
  }
  await db.update(submission_version).set(patch).where(eq(submission_version.id, versionId));
}

export type ReviewOutcome = "returned" | "approved" | "rejected";

/**
 * El REVISOR sella feedback + outcome sobre una versión pendiente. `feedback_by`
 * lo fuerza el servidor; aquí optimista con el propio userId para reflejar en la
 * UI sin esperar al sync-back.
 */
export async function reviewVersionMutation(
  db: PowerSyncDrizzleDB,
  params: {
    versionId: string;
    reviewerUserId: string;
    outcome: ReviewOutcome;
    feedback?: string | null;
  },
): Promise<void> {
  const now = nowISO();
  await db
    .update(submission_version)
    .set({
      feedback: params.feedback ?? null,
      feedback_by: params.reviewerUserId,
      feedback_at: now,
      outcome: params.outcome,
      updated_at: now,
    })
    .where(eq(submission_version.id, params.versionId));
}
